import { createServerFn } from "@tanstack/react-start"
import { eq, inArray, or, sql } from "drizzle-orm"
import { z } from "zod"

import { liveAssessmentOutcomes } from "@/constants/terminal/assessment-outcome"
import {
  terminalCompany,
  terminalIdentityDecision,
  terminalIdentityReview,
  terminalIndividual,
  terminalIndividualMention,
} from "@/db/schema/terminal-company-identity"
import {
  terminalAssessment,
  terminalAssessmentIndividualMention,
} from "@/db/schema/terminal-file"
import {
  terminalSource,
  terminalSourceCheck,
  terminalSourceRecord,
  terminalSourceRecordExtraction,
} from "@/db/schema/terminal-research"
import { db } from "@/lib/admin-db"

// The inspector reads one consistent graph snapshot. The server function also
// checks admin auth because its endpoint can be called without visiting a route.
export const loadIndividualInspector = createServerFn({ method: "GET" })
  .inputValidator(z.object({ individualId: z.string().uuid() }).strict())
  .handler(async ({ data }) => {
    const { assertAdminContext } = await import("@/lib/supabase/auth")
    await assertAdminContext()
    return db.transaction(
      async (tx) => {
        const [requested] = await tx
          .select({
            id: terminalIndividual.id,
            duplicateOf: terminalIndividual.duplicate_of_individual_id,
          })
          .from(terminalIndividual)
          .where(eq(terminalIndividual.id, data.individualId))
        if (!requested) return null
        const canonicalId = requested.duplicateOf ?? requested.id
        const individuals = await tx
          .select({
            id: terminalIndividual.id,
            duplicateOf: terminalIndividual.duplicate_of_individual_id,
            reviewId: terminalIndividual.duplicate_identity_review_id,
            ruleKey: terminalIndividual.duplicate_individual_match_rule_key,
            sourceRecordId: terminalIndividual.duplicate_source_record_id,
          })
          .from(terminalIndividual)
          .where(
            or(
              eq(terminalIndividual.id, canonicalId),
              eq(terminalIndividual.duplicate_of_individual_id, canonicalId)
            )
          )
        const individualIds = individuals.map(({ id }) => id)
        const mentions = await tx
          .select({
            id: terminalIndividualMention.id,
            individualId: terminalIndividualMention.individual_id,
            platformKey: terminalSource.platform_key,
            sourceKey: terminalSource.source_key,
            sourceRecordKey: terminalIndividualMention.source_record_key,
            holderKey: terminalIndividualMention.holder_key,
            authorityPersonKey: terminalIndividualMention.authority_person_key,
          })
          .from(terminalIndividualMention)
          .innerJoin(
            terminalSource,
            eq(terminalSource.id, terminalIndividualMention.source_id)
          )
          .where(
            inArray(terminalIndividualMention.individual_id, individualIds)
          )
        const mentionIds = mentions.map(({ id }) => id)
        const reviews = await tx
          .select({
            id: terminalIdentityReview.id,
            reason: terminalIdentityReview.reason,
            createdAt: terminalIdentityReview.created_at,
            resolvedAt: terminalIdentityReview.resolved_at,
            adjudication: terminalIdentityReview.adjudication,
            decisionKind: terminalIdentityDecision.kind,
            decisionRationale: terminalIdentityDecision.rationale,
          })
          .from(terminalIdentityReview)
          .leftJoin(
            terminalIdentityDecision,
            eq(
              terminalIdentityDecision.id,
              terminalIdentityReview.identity_decision_id
            )
          )
          .where(
            or(
              inArray(
                terminalIdentityReview.candidate_individual_id,
                individualIds
              ),
              ...(mentionIds.length
                ? [
                    inArray(
                      terminalIdentityReview.individual_mention_id,
                      mentionIds
                    ),
                  ]
                : [])
            )
          )
        const frozen = mentionIds.length
          ? await tx
              .select({
                mentionId:
                  terminalAssessmentIndividualMention.individual_mention_id,
                sourceRecordId:
                  terminalAssessmentIndividualMention.source_record_id,
                assessmentId: terminalAssessment.id,
                completedAt: terminalAssessment.completed_at,
                // Only a completed Assessment's parts reach the buyer.
                isLive: sql<boolean>`coalesce(${inArray(terminalAssessment.outcome, liveAssessmentOutcomes)}, false)`,
                companyId: terminalAssessment.company_id,
                companyName: terminalCompany.display_name,
                companyRunId: terminalAssessment.company_run_id,
                sourceCompanyRunId: terminalSourceCheck.company_run_id,
                researchTaskId: terminalSourceCheck.research_task_id,
              })
              .from(terminalAssessmentIndividualMention)
              .innerJoin(
                terminalAssessment,
                eq(
                  terminalAssessment.id,
                  terminalAssessmentIndividualMention.assessment_id
                )
              )
              .innerJoin(
                terminalCompany,
                eq(terminalCompany.id, terminalAssessment.company_id)
              )
              .innerJoin(
                terminalSourceRecord,
                eq(
                  terminalSourceRecord.id,
                  terminalAssessmentIndividualMention.source_record_id
                )
              )
              .innerJoin(
                terminalSourceRecordExtraction,
                eq(
                  terminalSourceRecordExtraction.id,
                  terminalSourceRecord.source_record_extraction_id
                )
              )
              .innerJoin(
                terminalSourceCheck,
                eq(
                  terminalSourceCheck.id,
                  terminalSourceRecordExtraction.source_check_id
                )
              )
              .where(
                inArray(
                  terminalAssessmentIndividualMention.individual_mention_id,
                  mentionIds
                )
              )
          : []
        return {
          canonicalId,
          individuals,
          mentions,
          reviews,
          frozen,
          // The Companies whose live File names the Individual.
          companyDegree: new Set(
            frozen.flatMap(({ companyId, isLive }) =>
              isLive ? [companyId] : []
            )
          ).size,
        }
      },
      { accessMode: "read only" }
    )
  })

export type IndividualInspector = NonNullable<
  Awaited<ReturnType<typeof loadIndividualInspector>>
>
